import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import {
  addDays,
  fmtHM,
  fmtISO,
  fmtLong,
  roundUpTo15,
  snapTo15,
} from '../lib/format.js';
import { EpicSearch } from './EpicSearch.jsx';
import { HoursInput } from './HoursInput.jsx';
import { Icon } from './Icon.jsx';
import {
  ImportSplitAction,
  RowStatusChip,
  rowImportStatus,
} from './ImportRowStatus.jsx';
import { MinutesSegment } from './MinutesSegment.jsx';
import { Select } from './Select.jsx';
import { ClientSelect } from './ClientSelect.jsx';

const DISMISS_TIMEOUT_MS = 5000;

function timeLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function durationMin(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const s = new Date(startIso).getTime();
  const e = new Date(endIso).getTime();
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return 0;
  return Math.round((e - s) / 60000);
}

function eventDefaultMinutes(event) {
  // v2.0: round event duration UP to the next 15-min step. 23 min → 30,
  // 46 min → 60. All-day / no-end events still default to 8h (already on
  // the grid). Cap at 8h so a multi-day event doesn't overflow the entry.
  if (event.allDay || !event.end) return 8 * 60;
  return Math.min(8 * 60, roundUpTo15(durationMin(event.start, event.end)));
}

function TimeChip({ event }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 6,
        fontVariantNumeric: 'tabular-nums',
        minWidth: 120,
      }}
    >
      {event.allDay ? (
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--sp-fg-2)',
          }}
        >
          All-day
        </span>
      ) : !event.end ? (
        <>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--sp-fg-1)' }}>
            {timeLabel(event.start)}
          </span>
          <span style={{ fontSize: 12, color: '#B57651', fontWeight: 600 }}>· no end</span>
        </>
      ) : (
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--sp-fg-1)' }}>
          {timeLabel(event.start)}{' '}
          <span style={{ color: 'var(--sp-fg-3)', fontWeight: 500 }}>–</span>{' '}
          {timeLabel(event.end)}
        </span>
      )}
    </div>
  );
}

function EventRow({ event, draft, dept, dictionary, onUpdate, onDismiss }) {
  const activityOptions = dictionary?.activitiesByDept?.[dept] || [];
  const activityMeta = dictionary?.activityMeta || {};
  const defaults = dictionary?.defaultFieldRules || {
    product: 'required',
    client: 'hidden',
    epic: 'hidden',
  };
  const descFor = (name) =>
    activityMeta[`${dept}::${name}`]?.description || '';
  const fieldRules = draft.activity
    ? activityMeta[`${dept}::${draft.activity}`]?.fieldRules || defaults
    : null;
  const hasActivity = !!draft.activity;
  // Mirror the design: row 2 always renders three field slots; each is
  // disabled when the picked activity excludes it (or before any pick).
  const productEnabled = hasActivity && fieldRules.product !== 'hidden';
  const clientEnabled = hasActivity && fieldRules.client !== 'hidden';
  const epicEnabled = hasActivity && fieldRules.epic !== 'hidden';
  const products = dictionary?.products || ['SwiftPay', 'SwiftGuard', 'None'];
  const clients = dictionary?.clients || [];

  if (event.alreadyImported) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '14px 16px',
          borderRadius: 12,
          background: 'var(--sp-bg-page)',
          opacity: 0.7,
          border: '1px dashed var(--sp-border)',
        }}
      >
        <TimeChip event={event} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--sp-fg-2)',
              textDecoration: 'line-through',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {event.title}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--sp-fg-3)',
              marginTop: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="check" size={12} stroke={2.4} /> Already logged · skipped
          </div>
        </div>
      </div>
    );
  }

  const included = draft.included;
  const mins =
    (parseInt(draft.hours, 10) || 0) * 60 +
    (parseInt(draft.minutes, 10) || 0);
  const status = rowImportStatus(draft, {
    alreadyLogged: false,
    requiresProduct: hasActivity
      ? fieldRules.product === 'required'
      : defaults.product === 'required',
  });

  return (
    <div
      data-event-id={event.id}
      style={{
        border: '1px solid var(--sp-border)',
        borderRadius: 12,
        background: included ? '#fff' : 'var(--sp-bg-page)',
        opacity: included ? 1 : 0.6,
        transition: 'opacity .15s, background .15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px 12px' }}>
        <input
          type="checkbox"
          checked={included}
          onChange={(e) => onUpdate({ included: e.target.checked })}
          title={included ? 'Skip this event' : 'Include this event'}
          style={{
            width: 16,
            height: 16,
            marginTop: 4,
            accentColor: '#05E6C5',
            cursor: 'pointer',
            flex: 'none',
          }}
        />
        <TimeChip event={event} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            type="text"
            value={draft.description}
            onChange={(e) => onUpdate({ description: e.target.value.slice(0, 200) })}
            placeholder="Description"
            disabled={!included}
            style={{
              width: '100%',
              border: 0,
              outline: 'none',
              background: 'transparent',
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--sp-fg-1)',
              padding: 0,
            }}
          />
        </div>
        {included && (
          <span style={{ marginTop: 2, flex: 'none' }}>
            <RowStatusChip status={status} duration={fmtHM(mins)} />
          </span>
        )}
        <button
          className="btn btn-ghost btn-icon"
          title="Never show this meeting again"
          onClick={() => onDismiss(event)}
          style={{ flex: 'none' }}
        >
          <Icon name="trash" size={14} />
        </button>
      </div>

      {included && (
        <div
          style={{
            padding: '0 16px 16px',
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 10,
          }}
        >
          {/* Row 1: Time + Activity */}
          <div style={{ gridColumn: 'span 4' }}>
            <label className="tt-field-label" style={{ fontSize: 11 }}>Time</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
              <HoursInput
                value={draft.hours}
                onChange={(v) => onUpdate({ hours: v })}
                size="sm"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <MinutesSegment
                  value={draft.minutes}
                  onChange={(v) => onUpdate({ minutes: v })}
                  size="sm"
                />
              </div>
            </div>
          </div>

          <div style={{ gridColumn: 'span 8' }}>
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Activity <span className="tt-field-required">*</span>
            </label>
            <Select
              value={draft.activity}
              options={activityOptions.map((a) => ({
                key: a,
                label: a,
                desc: descFor(a),
              }))}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              getDescription={(o) => o.desc}
              placeholder="Pick activity"
              onChange={(v) => onUpdate({ activity: v })}
              searchable
              searchPlaceholder="Search activities…"
              searchNoun="activity"
            />
          </div>

          {/* Row 2: Product · Client · Epic — always rendered, disabled when N/A */}
          <div style={{ gridColumn: 'span 4', opacity: productEnabled ? 1 : 0.5 }}>
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Product
              {productEnabled && fieldRules.product === 'required' && (
                <span className="tt-field-required">*</span>
              )}
            </label>
            <Select
              value={productEnabled ? draft.product : ''}
              options={products.map((p) => ({ key: p, label: p }))}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              placeholder={hasActivity ? 'Not applicable' : 'Pick activity first'}
              onChange={(v) => onUpdate({ product: v })}
              disabled={!productEnabled}
            />
          </div>

          <div style={{ gridColumn: 'span 4', opacity: clientEnabled ? 1 : 0.5 }}>
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Client
              {clientEnabled && fieldRules.client === 'required' && (
                <span className="tt-field-required">*</span>
              )}
            </label>
            <ClientSelect
              value={clientEnabled ? draft.client : null}
              clients={clients}
              placeholder="Pick a client"
              emptyLabel="No client"
              onChange={(v) => onUpdate({ client: v })}
              disabled={!clientEnabled}
              disabledLabel={hasActivity ? 'Not applicable' : 'Pick activity first'}
              searchPlaceholder="Search clients…"
            />
          </div>

          <div style={{ gridColumn: 'span 4', opacity: epicEnabled ? 1 : 0.5 }}>
            <label className="tt-field-label" style={{ fontSize: 11 }}>
              Jira epic
              {epicEnabled && fieldRules.epic === 'required' && (
                <span className="tt-field-required">*</span>
              )}
            </label>
            {epicEnabled ? (
              <EpicSearch
                value={draft.epic?.id}
                valueTitle={draft.epic?.title}
                onChange={(v) => onUpdate({ epic: v })}
              />
            ) : (
              <button
                type="button"
                className="tt-input"
                disabled
                style={{
                  textAlign: 'left',
                  color: 'var(--sp-fg-4)',
                  cursor: 'not-allowed',
                }}
              >
                {hasActivity ? 'Not applicable' : 'Pick activity first'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AuthGate({ onClose }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
      }}
    >
      <div
        style={{
          maxWidth: 460,
          background: '#fff',
          border: '1px solid var(--sp-border)',
          borderRadius: 18,
          padding: '32px 32px 28px',
          textAlign: 'center',
          boxShadow: 'var(--sp-shadow-md)',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            width: 56,
            height: 56,
            borderRadius: 16,
            background: 'var(--sp-mint-100)',
            color: '#007C7C',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 18,
          }}
        >
          <Icon name="cal" size={26} />
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>
          Connect your Google Calendar
        </h2>
        <p style={{ margin: '0 0 24px', fontSize: 14, lineHeight: '22px', color: 'var(--sp-fg-2)' }}>
          ClockIn will read meetings from your <strong>@swiftpay.ph</strong> calendar to pre-fill time entries.{' '}
          <strong>Read-only</strong> — we never create, edit, or delete events.
        </p>
        <a
          href="/auth/calendar/connect"
          style={{
            width: '100%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: '12px 16px',
            border: '1px solid var(--sp-border)',
            borderRadius: 10,
            background: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'none',
            color: 'var(--sp-fg-1)',
          }}
        >
          <Icon name="google" size={18} /> Grant calendar access
        </a>
        <div
          style={{
            marginTop: 18,
            fontSize: 12,
            color: 'var(--sp-fg-3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="lock" size={12} /> Scope: calendar.events.readonly
        </div>
        <div style={{ marginTop: 18, borderTop: '1px solid var(--sp-border-soft)', paddingTop: 14 }}>
          <button onClick={onClose} className="btn btn-ghost btn-sm">
            Back to Today
          </button>
        </div>
      </div>
    </div>
  );
}

function HiddenView({ ignoreList, restoring, onRestore, onBack }) {
  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '24px 32px 48px',
        maxWidth: 720,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <button onClick={onBack} className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }}>
        <Icon name="chevD" size={14} stroke={2} /> Back to import
      </button>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700 }}>Hidden meetings</h2>
      <p style={{ margin: '0 0 20px', color: 'var(--sp-fg-2)', fontSize: 14 }}>
        Events you've permanently dismissed. Restore to see them again in your import list.
        Recurring events stay hidden across every future occurrence until restored.
      </p>
      {ignoreList.length === 0 ? (
        <div
          className="card"
          style={{
            padding: 32,
            textAlign: 'center',
            borderRadius: 14,
            color: 'var(--sp-fg-3)',
            fontSize: 14,
          }}
        >
          Nothing hidden. Use the trash icon next to an event to dismiss it permanently.
        </div>
      ) : (
        <div className="card" style={{ padding: 8, borderRadius: 14 }}>
          {ignoreList.map((item, i) => {
            const hasTitle = Boolean(item.title);
            const label = hasTitle ? item.title : 'Hidden meeting (title not recorded)';
            // Older rows (pre-2026-05) were saved before we started
            // persisting titles — surface the fact so the user isn't
            // staring at an opaque ID. Restore still works the same.
            const sub = hasTitle
              ? null
              : `This meeting was hidden before titles were tracked. Restore it to see the meeting name again next time it appears on your calendar. (ref: ${item.eventId})`;
            return (
              <React.Fragment key={item.eventId}>
                {i > 0 && <hr className="tt-divider" style={{ margin: '0 12px' }} />}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: hasTitle ? 'var(--sp-fg-1)' : 'var(--sp-fg-2)',
                        fontStyle: hasTitle ? 'normal' : 'italic',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={hasTitle ? item.title : item.eventId}
                    >
                      {label}
                    </div>
                    {sub && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          color: 'var(--sp-fg-3)',
                          lineHeight: 1.4,
                        }}
                      >
                        {sub}
                      </div>
                    )}
                  </div>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => onRestore(item.eventId)}
                    disabled={restoring === item.eventId}
                  >
                    {restoring === item.eventId ? 'Restoring…' : 'Restore'}
                  </button>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Full-screen Calendar Import takeover. Sits on top of Today; opened
 * from the secondary "Import from Calendar" button. Fetches the user's
 * primary-calendar events for the selected date, lets them pick which
 * to import as time entries (each row gets an inline mini-form), and
 * commits them via repeated POST /api/entries calls stamped with
 * source = "calendar:<eventId>" so re-opens flag them as already
 * imported.
 *
 * Dismissals are soft — the user sees an "Undo" toast for 5s before we
 * call /api/calendar/ignores. The whole recurring series is keyed by
 * the event's seriesId so a single dismiss covers every occurrence.
 */
export function CalendarImport({
  open,
  onClose,
  user,
  selectedDate,
  dictionary,
  onImported,
}) {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [pendingDismiss, setPendingDismiss] = useState(null);
  const [ignoreList, setIgnoreList] = useState([]);
  const [restoring, setRestoring] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const refreshIgnoreList = useCallback(async () => {
    try {
      const data = await api.calendarIgnoreList();
      setIgnoreList(Array.isArray(data?.items) ? data.items : []);
    } catch {
      // Soft-fail: leave the current list in place. The user can still
      // dismiss new events; restore just won't show server-persisted ones.
    }
  }, []);

  const iso = useMemo(() => fmtISO(selectedDate), [selectedDate]);

  const initDrafts = useCallback((list) => {
    const init = {};
    list.forEach((e) => {
      if (e.alreadyImported) return;
      const defMin = eventDefaultMinutes(e);
      init[e.id] = {
        included: true,
        description: e.title || '',
        activity: '',
        product: '',
        client: null,
        epic: null,
        hours: Math.floor(defMin / 60),
        minutes: defMin % 60,
      };
    });
    return init;
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const s = await api.calendarStatus();
      setStatus(s);
    } catch {
      setStatus({ configured: false, connected: false });
    }
  }, []);

  const refreshEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.calendarEvents(iso);
      if (data.connected === false) {
        setStatus((s) => ({ ...(s || {}), connected: false }));
        setEvents([]);
      } else {
        const list = data.events || [];
        setEvents(list);
        setDrafts(initDrafts(list));
      }
    } catch {
      setError('Could not load calendar events.');
    } finally {
      setLoading(false);
    }
  }, [iso, initDrafts]);

  useEffect(() => {
    if (!open) return;
    refreshStatus();
  }, [open, refreshStatus]);

  useEffect(() => {
    if (!open) return;
    if (status?.connected) {
      refreshEvents();
      refreshIgnoreList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, status?.connected, iso]);

  // Auto-dismiss the pending-dismiss after the undo window expires.
  useEffect(() => {
    if (!pendingDismiss) return undefined;
    const t = setTimeout(async () => {
      const ev = pendingDismiss.event;
      // The server resolves "which id to persist" the same way it does
      // for filtering: seriesId wins over id so a single ignore hides
      // every future occurrence. Mirror that here for the local list.
      const persistedId = ev.seriesId || ev.id;
      try {
        await api.calendarIgnore(ev.id, ev.seriesId, ev.title);
        setIgnoreList((arr) =>
          arr.some((r) => r.eventId === persistedId)
            ? arr
            : [
                ...arr,
                {
                  eventId: persistedId,
                  title: ev.title || '',
                  ignoredAt: new Date().toISOString(),
                },
              ],
        );
        setEvents((es) =>
          es.filter(
            (e) => e.id !== ev.id && e.seriesId !== ev.seriesId,
          ),
        );
      } catch {
        setError('Could not hide event — please try again.');
      } finally {
        setPendingDismiss(null);
      }
    }, DISMISS_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [pendingDismiss]);

  const handleRestore = useCallback(
    async (eventId) => {
      setRestoring(eventId);
      setError(null);
      try {
        await api.calendarRestore(eventId);
        setIgnoreList((arr) => arr.filter((r) => r.eventId !== eventId));
        // Pull the events list again so any restored event for the
        // current day reappears immediately.
        refreshEvents();
      } catch {
        setError('Could not restore event — please try again.');
      } finally {
        setRestoring(null);
      }
    },
    [refreshEvents],
  );

  if (!open) return null;

  if (status === null) {
    // First-paint state — show a tiny loader so the screen isn't blank.
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          background: 'var(--sp-bg-page)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--sp-fg-3)',
          gap: 10,
        }}
      >
        <span className="tt-spinner sm" /> Loading calendar…
      </div>
    );
  }

  if (!status.configured || !status.connected) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          background: 'var(--sp-bg-page)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'tt-fade-in 200ms ease',
        }}
      >
        <Header onClose={onClose} selectedDate={selectedDate} />
        <AuthGate onClose={onClose} />
      </div>
    );
  }

  if (showHidden) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          background: 'var(--sp-bg-page)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'tt-fade-in 200ms ease',
        }}
      >
        <Header onClose={onClose} selectedDate={selectedDate} />
        <HiddenView
          ignoreList={ignoreList}
          restoring={restoring}
          onRestore={handleRestore}
          onBack={() => setShowHidden(false)}
        />
      </div>
    );
  }

  const updateDraft = (id, patch) =>
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const handleDismiss = (event) => {
    setPendingDismiss({ event });
  };

  const undoDismiss = () => setPendingDismiss(null);

  const activityMeta = dictionary?.activityMeta || {};
  const defaults = dictionary?.defaultFieldRules || {
    product: 'required',
    client: 'hidden',
    epic: 'hidden',
  };

  // Importable = included, has activity, satisfies required rules, time > 0
  const importable = events.filter((e) => {
    if (e.alreadyImported) return false;
    if (pendingDismiss?.event.id === e.id) return false;
    const d = drafts[e.id];
    if (!d || !d.included || !d.activity) return false;
    const rules =
      activityMeta[`${user.department}::${d.activity}`]?.fieldRules || defaults;
    if (rules.product === 'required' && !d.product) return false;
    if (rules.client === 'required' && !d.client) return false;
    if (rules.epic === 'required' && !d.epic?.id) return false;
    const mins = (parseInt(d.hours, 10) || 0) * 60 + (parseInt(d.minutes, 10) || 0);
    if (!mins) return false;
    return true;
  });

  const incomplete = events.filter((e) => {
    if (e.alreadyImported) return false;
    if (pendingDismiss?.event.id === e.id) return false;
    const d = drafts[e.id];
    return d && d.included && !importable.includes(e);
  });

  const totalMins = importable.reduce((s, e) => {
    const d = drafts[e.id];
    return s + (parseInt(d.hours, 10) || 0) * 60 + (parseInt(d.minutes, 10) || 0);
  }, 0);

  const jumpToFirstBlocker = () => {
    if (incomplete.length === 0 || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(
      `[data-event-id="${incomplete[0].id}"]`,
    );
    if (!el) return;
    const top = el.offsetTop - 16;
    scrollRef.current.scrollTo({ top, behavior: 'smooth' });
    setTimeout(() => {
      const focusable = el.querySelector(
        'button[role="combobox"], button, input:not([disabled])',
      );
      if (focusable && typeof focusable.focus === 'function') focusable.focus();
    }, 320);
  };

  const handleConfirm = async () => {
    if (importable.length === 0 || importing) return;
    setImporting(true);
    setError(null);
    let created = 0;
    for (const event of importable) {
      const d = drafts[event.id];
      const rules =
        activityMeta[`${user.department}::${d.activity}`]?.fieldRules ||
        defaults;
      const productOut = rules.product !== 'hidden' ? d.product || '' : '';
      const clientOut = rules.client !== 'hidden' ? d.client || '' : '';
      const epicId = rules.epic !== 'hidden' ? d.epic?.id || '' : '';
      const epicTitle = rules.epic !== 'hidden' ? d.epic?.title || '' : '';
      try {
        await api.createEntry({
          date: iso,
          activityName: d.activity,
          product: productOut,
          clientName: clientOut,
          epicId,
          epicTitle,
          hours: parseInt(d.hours, 10) || 0,
          // v2.0: defensive snap before save — every draft already comes from
          // MinutesSegment, but rounding belt-and-braces matches what the
          // server will accept and avoids surprising 400s for stale state.
          minutes: snapTo15(d.minutes),
          description: (d.description || '').trim(),
          calendarEventId: event.id,
        });
        created += 1;
      } catch (err) {
        setError(
          err?.status === 503
            ? 'Server busy — some entries may not have been saved.'
            : 'Could not import some events.',
        );
        break;
      }
    }
    setImporting(false);
    if (created > 0) {
      onImported?.(created);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'var(--sp-bg-page)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'tt-fade-in 200ms ease',
      }}
      data-screen-label="Import from Calendar"
    >
      <Header onClose={onClose} selectedDate={selectedDate} />

      <div
        ref={scrollRef}
        style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 28px' }}
      >
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          {error && (
            <div className="tt-error-msg" style={{ marginBottom: 12 }}>
              <Icon name="alert" size={12} /> {error}
            </div>
          )}

          {loading ? (
            <div
              className="card"
              style={{
                padding: 18,
                borderRadius: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div
                style={{
                  color: 'var(--sp-fg-3)',
                  fontSize: 13,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span className="tt-spinner sm" /> Fetching your calendar…
              </div>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div className="tt-skel" style={{ width: 80, height: 32 }} />
                  <div className="tt-skel" style={{ flex: 1, height: 18 }} />
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="card" style={{ padding: 40, borderRadius: 16, textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: 'var(--sp-bg-muted)',
                  color: 'var(--sp-fg-3)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                }}
              >
                <Icon name="cal" size={22} />
              </div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                No meetings on this day
              </h3>
              <p style={{ margin: '6px 0 0', color: 'var(--sp-fg-3)', fontSize: 14 }}>
                Try another date, or log your time manually from the Today view.
              </p>
            </div>
          ) : (
            <>
              <div
                style={{
                  marginBottom: 14,
                  padding: '10px 14px',
                  borderRadius: 10,
                  background: 'var(--sp-mint-100)',
                  color: '#026153',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Icon name="info" size={14} />
                <span>
                  <strong>
                    {events.length} meeting{events.length === 1 ? '' : 's'} found.
                  </strong>{' '}
                  Time and description are pre-filled from your calendar — pick an
                  activity and product to import.
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {events.map((event) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    draft={drafts[event.id] || {}}
                    dept={user.department}
                    dictionary={dictionary}
                    onUpdate={(patch) => updateDraft(event.id, patch)}
                    onDismiss={handleDismiss}
                  />
                ))}
              </div>

              <div style={{ marginTop: 18, textAlign: 'center' }}>
                <button
                  onClick={() => setShowHidden(true)}
                  className="tt-link"
                  style={{
                    background: 'none',
                    border: 0,
                    fontSize: 13,
                    color: 'var(--sp-fg-3)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Icon name="lock" size={12} /> Manage hidden meetings (
                  {ignoreList.length})
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <footer
        style={{
          padding: '14px 28px',
          borderTop: '1px solid var(--sp-border)',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flex: 'none',
          position: 'relative',
        }}
      >
        {pendingDismiss && (
          <div
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 12px)',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#1B1F23',
              color: '#fff',
              padding: '10px 12px 10px 16px',
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 13,
              boxShadow: 'var(--sp-shadow-md)',
              animation: 'tt-fade-in 160ms ease',
            }}
          >
            <span>
              Hid <strong>{pendingDismiss.event.title}</strong> from future imports
            </span>
            <button
              onClick={undoDismiss}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.3)',
                color: '#fff',
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Undo
            </button>
          </div>
        )}
        <div style={{ flex: 1, fontSize: 13, color: 'var(--sp-fg-2)' }}>
          {importable.length === 0 && incomplete.length === 0 ? (
            <span style={{ color: 'var(--sp-fg-3)' }}>Nothing selected to import</span>
          ) : importable.length === 0 ? (
            <span style={{ color: 'var(--sp-apricot-ink)', fontWeight: 600 }}>
              {incomplete.length} event{incomplete.length === 1 ? '' : 's'} need an activity to import
            </span>
          ) : (
            <span>
              <strong>{importable.length}</strong> ready ·{' '}
              <strong>{fmtHM(totalMins)}</strong> total
              {incomplete.length > 0 && (
                <>
                  {' '}
                  · <strong style={{ color: 'var(--sp-apricot-ink)' }}>{incomplete.length}</strong> incomplete
                </>
              )}
            </span>
          )}
        </div>
        <button className="btn btn-ghost" onClick={onClose} disabled={importing}>
          Cancel
        </button>
        {importing ? (
          <button
            className="btn btn-primary"
            disabled
            style={{ whiteSpace: 'nowrap' }}
          >
            <span className="tt-spinner sm" style={{ marginRight: 6 }} />
            Importing…
          </button>
        ) : (
          <ImportSplitAction
            readyCount={importable.length}
            incompleteCount={incomplete.length}
            tone="mint"
            noun="entry"
            nounPlural="entries"
            onImport={handleConfirm}
            onImportAndSkip={handleConfirm}
            onJumpToBlocker={jumpToFirstBlocker}
          />
        )}
      </footer>
    </div>
  );
}

function Header({ onClose, selectedDate }) {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '16px 28px',
        borderBottom: '1px solid var(--sp-border)',
        background: '#fff',
        flex: 'none',
      }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: 'var(--sp-mint-100)',
            color: '#007C7C',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="cal" size={16} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--sp-fg-3)',
            }}
          >
            Import from Calendar
          </span>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
            {fmtLong(selectedDate)}
          </span>
        </div>
      </div>
      <div style={{ flex: 1 }} />
      <button className="btn btn-ghost" onClick={onClose}>
        <Icon name="x" size={14} /> Close
      </button>
    </header>
  );
}
